import { useEffect } from "react"
import { useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"
import _ from "lodash-es"

import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon"
import PageTitle from "../../components/PageTitle.jsx"
import { NotificationTypes, addNotification } from "../../stores/notification-store"
import NewUserModal from "./components/NewUserModal.jsx"
import { SortDirectionType, UserRoles } from "../../utils/constants.js"

import useUsers from "../../hooks/useUsersList"

export default function UserList() {

    const { beId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData  } = useOutletContext()
    const { users, loading } = useUsers(beId, config, accessToken)

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUsers) {
            addNotification('You are not authorized to view users for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value])
    
    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditUsers
    })

    const showModalNewUserSignal = useSignal(false)

    const currentPage = useSignal(1)
    const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize')) || 250)
    const filterString = useSignal("")
    const sortColumn = useSignal('')

    const sortDirection = useSignal(SortDirectionType.DESCENDING)
    
    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

    const filteredData = useComputed(() => {
        if (!filterString.value) {
            return users.value ? [...users.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterString.value.toLowerCase()

        // Filter the data based on the filter text
        return users.value.filter(item => {
            // Limit search to these fields
            let searchData = {

            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedData = useComputed(() => {

        // just return filtered data if no sorting
        if (!sortColumn.value.length) {
            return [...filteredData.value]
        }

        let _sortedData = [...filteredData.value]
        return _sortedData.sort((a, b) => {
            let _a = _.get(a, sortColumn.value, null)
            let _b = _.get(b, sortColumn.value, null)

            // convert values to lowercase
            if (_a && _a.length) {
                _a = _a.toLowerCase()
            }
            if (_b && _b.length) {
                _b = _b.toLowerCase()
            }

            if (sortDirection.value == SortDirectionType.DESCENDING) {
                if (_a < _b) return -1
                if (_a > _b) return 1
                return 0
            }
            else {
                if (_a > _b) return -1
                if (_a < _b) return 1
                return 0
            }
        })
    })

    const totalPages = useComputed(() => {
        return Math.ceil(filteredData.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayData = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPage.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedData.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumber = useComputed(() => {
        return ((currentPage.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumber = useComputed(() => {
        const startIndex = (currentPage.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredData.value.length ? endIndex : filteredData.value.length
    })

    const accordionData = [
        { name: 'Users', data: users },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]


    const handlePageSizeSelect = (v) => {
        currentPage.value = 1
        rowsPerPageSignal.value = parseInt(v, 10)
    }
    /*const handleFilterStringChange = (e) => {
        currentPage.value = 1
        filterString.value = e.currentTarget.value
    }*/

    const handleCopyClipboard = item => {
        if (item) {
            // place the data on the users clipboard if the browser supports it
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                addNotification(`The data for ${item.email} has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
            }
            else {
                addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
            }
        }
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumn.value) {
            sortDirection.value = sortDirection.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumn.value = datafield
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumn.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirection.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPages.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPage.value} onClick={() => currentPage.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.id}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="10" className="text-center">No users found</td></tr></tbody>
            )
        }
    }

    const getUserRoleString = function (type) {
        switch (type) {
            case UserRoles.ReadOnly:
                return "Read Only"
            case UserRoles.Manager:
                return "Manager"
            case UserRoles.TechnicalAdvisor:
                return "Technical Advisor"
            case UserRoles.Technician:
                return "Technician"
            case UserRoles.HelpDesk:
                return "Help Desk Administrator"
            default:
                return "Error"
        }
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width="16" height="16"></Icon></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>

                <td>{item.username}</td>
                <td>{item.firstName}</td>
                <td>{item.lastName}</td>
                <td>{item.email}</td>
                <td>{item.emailAddressVerified ? 'Yes' : 'No'}</td>

                <td>{getUserRoleString(item.userType)}</td>
                <td>{item.isActive ? 'Yes' : 'No'}</td>

                <td>{item.isLocked ? 'Yes' : 'No'}</td>
                <td>{(!item.isInitialUser) ? 'Yes' : 'No'}</td>
            </tr>
        )
    }

    const showNewUserModal = function (event) {
        console.log("Showing the new user modal")
        showModalNewUserSignal.value = true
        console.log("You should see it now")
    }

    if (loading.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
            <PageTitle title='User List' canEditSignal={canEditSignal} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Stack direction="horizontal" gap={3}>
                    <DropdownButton variant="outline-secondary" title={`${rowsPerPageSignal} records per page`} id="pageSize-dropdown" onSelect={handlePageSizeSelect}>
                        <Dropdown.Item eventKey="10">10</Dropdown.Item>
                        <Dropdown.Item eventKey="25">25</Dropdown.Item>
                        <Dropdown.Item eventKey="50">50</Dropdown.Item>
                        <Dropdown.Item eventKey="250">250</Dropdown.Item>
                        <Dropdown.Item eventKey="500">500</Dropdown.Item>
                        <Dropdown.Item eventKey="1000">1000</Dropdown.Item>
                    </DropdownButton>
                </Stack>
                {canEditSignal.value && <Nav.Item className="mt-1">
                    <Button size="sm" variant="outline-secondary" onClick={showNewUserModal}><Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;Create User</Button>
                </Nav.Item>}
            </Nav>

            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Username'} sortable={true} datafield={'username'}></TableColumnHeader>
                        <TableColumnHeader title={'First Name'} sortable={true} datafield={'firstName'}></TableColumnHeader>
                        <TableColumnHeader title={'Last Name'} sortable={true} datafield={'lastName'}></TableColumnHeader>
                        <TableColumnHeader title={'Email Address'} sortable={true} datafield={'email'}></TableColumnHeader>
                        <TableColumnHeader title={'Email Verified'} sortable={true} datafield={'emailAddressVerified'}></TableColumnHeader>

                        <TableColumnHeader title={'Role'} sortable={true} datafield={'userType'}></TableColumnHeader>
                        <TableColumnHeader title={'Active'} sortable={true} datafield={'isActive'}></TableColumnHeader>

                        <TableColumnHeader title={'Account Locked'} sortable={true} datafield={'isLocked'}></TableColumnHeader>
                        <TableColumnHeader title={'Has Logged In'} sortable={true} datafield={'isInitialUser'}></TableColumnHeader>
                    </tr>
                </thead>
                <TableRows items={displayData.value} />
                <tfoot><tr><td className="fs-6" colSpan={10}>Displaying {startDisplayRowNumber.value}-{endDisplayRowNumber.value} of {filteredData.value.length}</td></tr></tfoot>
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debug} />
            <NewUserModal
                showSignal={showModalNewUserSignal}
            ></NewUserModal>
        </>
    )
}